import { fbt } from 'fbtee'
import { CalendarDays, Users } from 'lucide-react'
import { beaconUserFallback } from '../../lib/commonStrings'
import { useTransitionNavigate } from '../../lib/viewTransition'
import { useOpenEvent } from '../../hooks/useOpenEvent'
import { Section } from '../../components/Section'
import { FriendRequestRow } from '../../components/FriendRequestRow'
import { ThumbStack } from '../../components/profile/ThumbStack'
import { Button } from '../../components/ui/button'
import type { GroupInvite, UserProfile } from '../../types'
import type { CrewTicketAction } from '../../hooks/useCrewTicketActions'
import type { InboxSuggestion } from './useInboxSuggestions'
import { TicketInboxRow } from './TicketInboxRow'

/**
 * Everything waiting on the viewer — friend requests, group invites, events
 * suggested to groups they admin, and tickets to answer, claim or hand over —
 * at the top of Crew, with labeled buttons. Renders nothing when it's empty.
 */
export function CrewInbox({
  requestIds,
  profiles,
  profilesLoading,
  onAcceptRequest,
  onDeclineRequest,
  invites,
  onAcceptInvite,
  onDeclineInvite,
  suggestions,
  onApproveSuggestion,
  onDeclineSuggestion,
  viewerId,
  tickets,
  ticketProfiles,
}: {
  requestIds: string[]
  profiles: Record<string, UserProfile>
  profilesLoading: boolean
  onAcceptRequest: (uid: string) => void
  onDeclineRequest: (uid: string) => void
  invites: GroupInvite[]
  onAcceptInvite: (invite: GroupInvite) => void
  onDeclineInvite: (invite: GroupInvite) => void
  suggestions: InboxSuggestion[]
  onApproveSuggestion: (s: InboxSuggestion) => void
  onDeclineSuggestion: (s: InboxSuggestion) => void
  viewerId: string
  tickets: CrewTicketAction[]
  ticketProfiles: Record<string, UserProfile>
}) {
  const navigate = useTransitionNavigate()
  const openEvent = useOpenEvent()
  const count = requestIds.length + invites.length + suggestions.length + tickets.length
  if (count === 0) return null

  return (
    <Section title={<>{fbt('Waiting on you', 'Crew tab section with pending friend requests and group invites')} · {count}</>}>
      <ul className="flex flex-col gap-2 rounded-xl border border-border bg-card p-3">
        {requestIds.map(uid => (
          <FriendRequestRow
            key={uid}
            loading={!profiles[uid] && profilesLoading}
            name={profiles[uid]?.displayName?.trim() || beaconUserFallback()}
            profile={profiles[uid]}
            onOpen={() => navigate(`/users/${uid}`)}
            onAccept={() => onAcceptRequest(uid)}
            onDecline={() => onDeclineRequest(uid)}
          />
        ))}
        {invites.map(invite => (
          <li key={invite.id} className="flex items-center gap-2.5">
            <ThumbStack srcs={[invite.groupImageUrl]} icon={Users} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{invite.groupName}</span>
              <span className="block text-xs text-muted-foreground">
                {fbt('Group invite', 'Subtitle on a pending group invite row in Crew')}
              </span>
            </span>
            <Button size="sm" className="h-8" onClick={() => onAcceptInvite(invite)}>
              {fbt('Join', 'Button to accept a pending group invite')}
            </Button>
            <Button size="sm" variant="outline" className="h-8" onClick={() => onDeclineInvite(invite)}>
              {fbt('Decline', 'Button to decline a pending group invite')}
            </Button>
          </li>
        ))}
        {suggestions.map(s => (
          <li key={s.request.id} className="flex items-center gap-2.5" data-testid="crew-inbox-suggestion">
            <button
              type="button"
              className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 text-left"
              onClick={() => navigate(`/groups/${s.group.id}`, { state: { entryPoint: 'crew' } })}
            >
              <ThumbStack srcs={[s.event.imageUrl]} icon={CalendarDays} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{s.event.name}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  <fbt desc="Subtitle on a Crew inbox row: a member suggested this event to a group the viewer admins">
                    <fbt:param name="name">{s.requesterName}</fbt:param> suggested it to <fbt:param name="group">{s.group.name}</fbt:param>
                  </fbt>
                </span>
              </span>
            </button>
            <Button size="sm" className="h-8" onClick={() => onApproveSuggestion(s)}>
              {fbt('Approve', 'Button in the Crew inbox that approves an event suggested to a group and links it')}
            </Button>
            <Button size="sm" variant="outline" className="h-8" onClick={() => onDeclineSuggestion(s)}>
              {fbt('Decline', 'Button in the Crew inbox that declines an event suggested to a group')}
            </Button>
          </li>
        ))}
        {tickets.map(t => (
          <TicketInboxRow
            key={t.key}
            item={t}
            viewerId={viewerId}
            profile={ticketProfiles[t.userId]}
            name={ticketProfiles[t.userId]?.displayName?.trim() || beaconUserFallback()}
            onOpen={() => openEvent(t.event, undefined)}
          />
        ))}
      </ul>
    </Section>
  )
}
